import React, { forwardRef } from 'react';
import { motion, type Variants } from 'motion/react';
import twMerge from 'utilities/src/twMerge';
import IconComponent from '../Icon';
import OptionRow from './_OptionRow';
import { DESTINATIONS, type AiWidgetDirection } from './constants';

interface Props {
  id: string;
  titleId: string;
  label: string;
  prompt: string;
  direction: AiWidgetDirection;
  isOpen: boolean;
  onClose: () => void;
  onOptionClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  firstRowRef: React.RefObject<HTMLAnchorElement | null>;
}

const panelVariants: Variants = {
  hidden: {
    scale: 0.18,
    opacity: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { staggerChildren: 0.045, delayChildren: 0.12 },
  },
};

const AiWidgetPanel = forwardRef<HTMLDivElement, Props>(
  ({ id, titleId, label, prompt, direction, isOpen, onClose, onOptionClick, firstRowRef }, ref) => (
    <motion.div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-hidden={!isOpen}
      inert={!isOpen}
      id={id}
      className={twMerge(
        'shadow-lv4 bg-site-neutrals-900 text-site-neutrals-0 absolute left-0 z-10 w-[min(20.5rem,calc(100vw-2rem))] rounded-xl py-3',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
        {
          'bottom-0 origin-bottom-left': direction === 'toTop',
          'top-0 origin-top-left': direction === 'toBottom',
        },
      )}
      variants={panelVariants}
      initial="hidden"
      animate={isOpen ? 'visible' : 'hidden'}
      transition={{ duration: 0.46, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div className="flex min-h-6 items-center gap-2 px-3 pr-1">
        <motion.span
          className="flex size-6 shrink-0"
          initial={{ rotate: -120, scale: 0.4 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <IconComponent iconName="AiStar" size="IconM" className="bg-site-primary-600" />
        </motion.span>
        <div id={titleId} className="site-paragraph-s min-w-0 flex-1 truncate font-medium">
          {label}
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex size-6 shrink-0 items-center justify-center"
        >
          <IconComponent
            iconName="Close"
            size="IconM"
            className="bg-site-neutrals-200 hover:bg-site-neutrals-100 transition-colors duration-150"
          />
        </button>
      </div>
      <ul className="mt-3 flex flex-col gap-2 px-3">
        {DESTINATIONS.map((destination, index) => (
          <OptionRow
            key={destination.label}
            ref={index === 0 ? firstRowRef : undefined}
            destination={destination}
            prompt={prompt}
            onClick={onOptionClick}
          />
        ))}
      </ul>
    </motion.div>
  ),
);

AiWidgetPanel.displayName = 'AiSummaryWidgetPanel';

export default AiWidgetPanel;
